if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Reduced debounce: scroll-snap fires slideChanged once per swipe/click, 100 ms is enough
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // On resize, snap the current slide back to position instantly (avoids stale pixel offsets)
        if (this.mql.matches) {
          const resizeObserver = new ResizeObserver(() => {
            if (!this.mql.matches) return;
            const idx = this.getActiveIndex();
            if (idx > 0) this.setSliderPosition(idx, false);
          });
          resizeObserver.observe(this.elements.viewer);
        }
      }

      getMediaItems() {
        // Use direct children of the slider UL only — nested elements (zoom buttons,
        // deferred-media, product-model) also carry data-media-id and would inflate the index.
        const slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        return slider ? Array.from(slider.children).filter((el) => el.dataset.mediaId) : [];
      }

      getActiveIndex() {
        return this.getMediaItems().findIndex((el) => el.classList.contains('is-active'));
      }

      // smooth=true → animated; smooth=false → instant re-snap (used on resize)
      setSliderPosition(index, smooth = true) {
        const list = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (!list) return;
        // sliderItemOffset accounts for any CSS gap between slides; fall back to clientWidth when not yet initialised
        const offset = this.elements.viewer.sliderItemOffset || list.clientWidth;
        list.scrollTo({ left: index * offset, behavior: smooth ? 'smooth' : 'instant' });
      }

      // Fired by SliderComponent after the user navigates with prev/next arrows
      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        // Keep is-active in sync with the visible slide
        this.getMediaItems().forEach((el) => el.classList.remove('is-active'));
        event.detail.currentElement.classList.add('is-active');

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.getMediaItems().forEach((element) => element.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (this.mql.matches) {
          // Desktop: scroll the native slider to the target slide
          const idx = this.getActiveIndex();
          if (idx !== -1) this.setSliderPosition(idx);
        } else {
          // Mobile: prepend the active item so it appears first in the horizontal scroll container
          if (prepend) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }

            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }

          window.setTimeout(() => {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          });
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.mql.matches) {
          // Vertical thumbnail strip on desktop: scroll by top offset within the UL
          const list = this.elements.thumbnails.slider;
          if (!list) return;
          // thumbnail.offsetTop → relative to slider-component (position:relative)
          // list.offsetTop     → relative to same slider-component
          // difference = thumbnail's position inside the UL
          const scrollTarget = thumbnail.offsetTop - list.offsetTop;
          const alreadyVisible =
            scrollTarget >= list.scrollTop &&
            scrollTarget + thumbnail.offsetHeight <= list.scrollTop + list.clientHeight;
          if (!alreadyVisible) {
            list.scrollTo({ top: scrollTarget, behavior: 'smooth' });
          }
        } else {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
